import type { VehicleRow } from "@/lib/vehicle-queries";

/**
 * EPA vehicle classes split the same shopping segment into many labels
 * ("Standard Sport Utility Vehicle 2WD", "Sport Utility Vehicle - 4WD",
 * "Small Sport Utility Vehicle 4WD"). Shoppers cross-shop across those labels,
 * so segment matching is done on the family, not the raw string.
 */
export function classFamily(cls: string | null | undefined): string {
  const c = (cls ?? "").toLowerCase();
  if (!c) return "";
  if (c.includes("sport utility") || c.includes("suv")) return "suv";
  if (c.includes("pickup") || c.includes("truck")) return "pickup";
  if (c.includes("van") || c.includes("minivan")) return "van";
  if (c.includes("station wagon") || c.includes("wagon")) return "wagon";
  if (c.includes("cars")) return "car";
  return c;
}

/** Are two EPA classes close enough that a matchup page is worth indexing? */
export function sameSegment(a: string | null | undefined, b: string | null | undefined): boolean {
  const fa = classFamily(a);
  const fb = classFamily(b);
  return !!fa && fa === fb;
}

export interface SideSlugs {
  makeSlug: string;
  modelSlug: string;
}

/** Split "toyota-camry-vs-honda-accord" using the known make slugs. */
export function parsePairSlug(pair: string, makeSlugs: string[]): [SideSlugs, SideSlugs] | null {
  const parts = pair.split("-vs-");
  if (parts.length !== 2) return null;
  const sorted = [...makeSlugs].sort((a, b) => b.length - a.length);
  const sides = parts.map((p) => {
    const make = sorted.find((m) => p === m || p.startsWith(`${m}-`));
    if (!make || p === make) return null;
    return { makeSlug: make, modelSlug: p.slice(make.length + 1) };
  });
  if (!sides[0] || !sides[1]) return null;
  return [sides[0], sides[1]];
}

export function pairSlug(a: SideSlugs, b: SideSlugs): string {
  return `${a.makeSlug}-${a.modelSlug}-vs-${b.makeSlug}-${b.modelSlug}`;
}

/**
 * Both orientations of a matchup render the same table, so every internal
 * link must point at one agreed orientation. Without this, half the compare
 * library ends up as near-duplicate pages with a single incoming link each.
 */
export function canonicalPairSlug(a: SideSlugs, b: SideSlugs): string {
  const sa = `${a.makeSlug}-${a.modelSlug}`;
  const sb = `${b.makeSlug}-${b.modelSlug}`;
  return sa <= sb ? `${sa}-vs-${sb}` : `${sb}-vs-${sa}`;
}

/** Canonical form of an existing pair slug, or the input when unparseable. */
export function canonicalizePair(pair: string, makeSlugs: string[]): string {
  const parsed = parsePairSlug(pair, makeSlugs);
  if (!parsed) return pair;
  return canonicalPairSlug(parsed[0], parsed[1]);
}

export interface SideSummary {
  makeName: string;
  makeSlug: string;
  modelName: string;
  modelSlug: string;
  fullName: string;
  yearMin: number | null;
  yearMax: number | null;
  bestMpg: number | null;
  bestCity: number | null;
  bestHwy: number | null;
  lowestFuelCost: number | null;
  engines: string[];
  transmissions: string[];
  drives: string[];
  fuelTypes: string[];
  vehicleClass: string | null;
  lowestCo2: number | null;
  trimCount: number;
}

function uniq(values: (string | null)[], limit = 4): string[] {
  return [...new Set(values.filter((v): v is string => !!v))].slice(0, limit);
}

function best(values: (number | null)[], mode: "max" | "min"): number | null {
  const nums = values.filter((v): v is number => typeof v === "number" && v > 0);
  if (!nums.length) return null;
  return mode === "max" ? Math.max(...nums) : Math.min(...nums);
}

export function summarizeSide(
  makeName: string,
  makeSlug: string,
  modelName: string,
  modelSlug: string,
  vehicles: VehicleRow[],
): SideSummary {
  const years = vehicles.map((v) => v.year);
  const classCounts = new Map<string, number>();
  for (const v of vehicles) {
    if (v.vehicle_class) classCounts.set(v.vehicle_class, (classCounts.get(v.vehicle_class) ?? 0) + 1);
  }
  const vehicleClass =
    [...classCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  return {
    makeName,
    makeSlug,
    modelName,
    modelSlug,
    fullName: `${makeName} ${modelName}`,
    yearMin: years.length ? Math.min(...years) : null,
    yearMax: years.length ? Math.max(...years) : null,
    bestMpg: best(vehicles.map((v) => v.mpg_combined), "max"),
    bestCity: best(vehicles.map((v) => v.mpg_city), "max"),
    bestHwy: best(vehicles.map((v) => v.mpg_hwy), "max"),
    lowestFuelCost: best(vehicles.map((v) => v.annual_fuel_cost), "min"),
    engines: uniq(vehicles.map((v) => v.engine_desc ?? v.displacement)),
    transmissions: uniq(vehicles.map((v) => v.transmission), 3),
    drives: uniq(vehicles.map((v) => v.drive), 3),
    fuelTypes: uniq(vehicles.map((v) => v.fuel_type), 3),
    vehicleClass,
    lowestCo2: best(vehicles.map((v) => v.co2_tailpipe_gpm), "min"),
    trimCount: vehicles.length,
  };
}

export interface CompareRow {
  label: string;
  a: string;
  b: string;
  /** "a", "b" or null when neither wins or the metric isn't a contest. */
  winner: "a" | "b" | null;
  note?: string;
}

function pick(
  a: number | null,
  b: number | null,
  mode: "max" | "min",
): "a" | "b" | null {
  if (a == null || b == null || a === b) return null;
  if (mode === "max") return a > b ? "a" : "b";
  return a < b ? "a" : "b";
}

export function buildCompareRows(a: SideSummary, b: SideSummary): CompareRow[] {
  const fmt = (v: number | null, suffix = "") => (v == null ? "Not available" : `${v}${suffix}`);
  const money = (v: number | null) => (v == null ? "Not available" : `$${v.toLocaleString()}`);
  const list = (v: string[]) => (v.length ? v.join(", ") : "Not available");

  return [
    {
      label: "Best combined MPG",
      a: fmt(a.bestMpg, " mpg"),
      b: fmt(b.bestMpg, " mpg"),
      winner: pick(a.bestMpg, b.bestMpg, "max"),
    },
    {
      label: "Best city MPG",
      a: fmt(a.bestCity, " mpg"),
      b: fmt(b.bestCity, " mpg"),
      winner: pick(a.bestCity, b.bestCity, "max"),
    },
    {
      label: "Best highway MPG",
      a: fmt(a.bestHwy, " mpg"),
      b: fmt(b.bestHwy, " mpg"),
      winner: pick(a.bestHwy, b.bestHwy, "max"),
    },
    {
      label: "Lowest annual fuel cost",
      a: money(a.lowestFuelCost),
      b: money(b.lowestFuelCost),
      winner: pick(a.lowestFuelCost, b.lowestFuelCost, "min"),
      note: "EPA estimate at 15,000 miles per year",
    },
    {
      label: "Lowest CO2 output",
      a: a.lowestCo2 == null ? "Not available" : `${Math.round(a.lowestCo2)} g/mi`,
      b: b.lowestCo2 == null ? "Not available" : `${Math.round(b.lowestCo2)} g/mi`,
      winner: pick(a.lowestCo2, b.lowestCo2, "min"),
    },
    { label: "Engines offered", a: list(a.engines), b: list(b.engines), winner: null },
    {
      label: "Transmissions",
      a: list(a.transmissions),
      b: list(b.transmissions),
      winner: null,
    },
    { label: "Drivetrains", a: list(a.drives), b: list(b.drives), winner: null },
    { label: "Fuel types", a: list(a.fuelTypes), b: list(b.fuelTypes), winner: null },
    {
      label: "EPA class",
      a: a.vehicleClass ?? "Not available",
      b: b.vehicleClass ?? "Not available",
      winner: null,
    },
    {
      label: "Years covered",
      a: a.yearMin ? `${a.yearMin}–${a.yearMax}` : "Not available",
      b: b.yearMin ? `${b.yearMin}–${b.yearMax}` : "Not available",
      winner: null,
    },
    {
      label: "Configurations on record",
      a: `${a.trimCount}`,
      b: `${b.trimCount}`,
      winner: null,
    },
  ];
}

/** One-sentence extractable answer for the comparison page. */
export function compareAnswer(a: SideSummary, b: SideSummary): string {
  if (a.bestMpg && b.bestMpg && a.bestMpg !== b.bestMpg) {
    const win = a.bestMpg > b.bestMpg ? a : b;
    const lose = a.bestMpg > b.bestMpg ? b : a;
    const diff = Math.abs(a.bestMpg - b.bestMpg);
    const cost =
      win.lowestFuelCost && lose.lowestFuelCost
        ? ` and costs about $${Math.abs(
            lose.lowestFuelCost - win.lowestFuelCost,
          ).toLocaleString()} less per year to fuel`
        : "";
    return `The ${win.fullName} is the more efficient of the two: it reaches ${win.bestMpg} mpg combined versus ${lose.bestMpg} mpg for the ${lose.fullName}, a ${diff} mpg advantage${cost}.`;
  }
  if (a.bestMpg && b.bestMpg) {
    return `The ${a.fullName} and ${b.fullName} are evenly matched on fuel economy — both peak at ${a.bestMpg} mpg combined — so the decision comes down to engine choice, drivetrain, and class.`;
  }
  return `This comparison puts the ${a.fullName} and ${b.fullName} side by side on EPA fuel economy, engines, transmissions, drivetrain, and annual fuel cost.`;
}

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

/**
 * Data-derived analysis paragraphs for a matchup page.
 *
 * Semrush flagged comparison pages as thin next to rival pages. Every sentence
 * here is computed from the two vehicles' own EPA records — five-year fuel
 * spend, city/highway bias, powertrain breadth, drivetrain and class — so the
 * added length is model-specific rather than boilerplate.
 */
export function compareNarrative(a: SideSummary, b: SideSummary): string[] {
  const out: string[] = [];

  if (a.lowestFuelCost && b.lowestFuelCost) {
    const cheap = a.lowestFuelCost <= b.lowestFuelCost ? a : b;
    const dear = a.lowestFuelCost <= b.lowestFuelCost ? b : a;
    const gap = dear.lowestFuelCost! - cheap.lowestFuelCost!;
    out.push(
      `Running cost over five years: at the EPA's 15,000-mile-a-year assumption the ${cheap.fullName} burns about ${money(cheap.lowestFuelCost!)} of fuel annually against ${money(dear.lowestFuelCost!)} for the ${dear.fullName}. That ${money(gap)} yearly gap compounds to roughly ${money(gap * 5)} over five years and ${money(gap * 8)} over eight — often more than the price difference between two trims of the same vehicle, and the single largest running-cost line item separating these two.`,
    );
  }

  const cityGap = a.bestCity && b.bestCity ? a.bestCity - b.bestCity : null;
  const hwyGap = a.bestHwy && b.bestHwy ? a.bestHwy - b.bestHwy : null;
  if (cityGap !== null && hwyGap !== null) {
    const cityWin = cityGap > 0 ? a : b;
    const hwyWin = hwyGap > 0 ? a : b;
    if (cityWin.fullName !== hwyWin.fullName) {
      out.push(
        `City versus highway split: the ${cityWin.fullName} is the stronger commuter at ${cityWin.bestCity} mpg city, while the ${hwyWin.fullName} pulls ahead on the interstate at ${hwyWin.bestHwy} mpg highway. If most of your driving is stop-and-go traffic the city figure is the one to weigh; for long-distance highway mileage the reverse is true, and the two vehicles effectively swap places depending on how you drive.`,
      );
    } else {
      out.push(
        `City and highway economy: the ${cityWin.fullName} leads in both cycles — ${cityWin.bestCity} mpg city and ${cityWin.bestHwy} mpg highway against ${(cityWin === a ? b : a).bestCity} and ${(cityWin === a ? b : a).bestHwy} mpg — so its fuel-efficiency advantage holds whether you commute in traffic or cover long highway miles.`,
      );
    }
  }

  if (a.engines.length || b.engines.length) {
    out.push(
      `Powertrain choice: the ${a.fullName} is recorded with ${a.engines.length || "no"} distinct engine configuration${a.engines.length === 1 ? "" : "s"}${a.engines.length ? ` (${a.engines.slice(0, 3).join(", ")})` : ""}, the ${b.fullName} with ${b.engines.length || "no"}${b.engines.length ? ` (${b.engines.slice(0, 3).join(", ")})` : ""}. Across ${a.trimCount} recorded ${a.fullName} configurations and ${b.trimCount} for the ${b.fullName}, the wider the engine spread the more the real-world fuel efficiency depends on which version you actually buy, so check the specific model year and drivetrain rather than the headline number.`,
    );
  }

  const drives = [...new Set([...a.drives, ...b.drives])];
  if (drives.length) {
    out.push(
      `Drivetrain and class: the ${a.fullName} is offered with ${a.drives.join(", ") || "a single drivetrain"}, the ${b.fullName} with ${b.drives.join(", ") || "a single drivetrain"}. All-wheel drive typically costs one to two mpg combined against the same vehicle in front- or rear-wheel drive, which is worth remembering when the gap between these two is small. The EPA classes them as ${a.vehicleClass ?? "unclassified"} and ${b.vehicleClass ?? "unclassified"} respectively — the class drives the interior volume, cargo space and passenger space each one is measured against.`,
    );
  }

  if (a.lowestCo2 && b.lowestCo2) {
    const clean = a.lowestCo2 <= b.lowestCo2 ? a : b;
    const other = a.lowestCo2 <= b.lowestCo2 ? b : a;
    out.push(
      `Tailpipe emissions: at their cleanest configurations the ${clean.fullName} emits ${clean.lowestCo2} g/mile of CO2 versus ${other.lowestCo2} g/mile for the ${other.fullName}. Emissions track fuel consumption almost exactly, so the cleaner vehicle here is also the cheaper one to fuel.`,
    );
  }

  return out;
}
